import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm } from "@inertiajs/react"
import { IconArrowLeft } from "@tabler/icons-react"

export default function CustomerNew() {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    legal_name: "",
    alias: "",
    tax_no: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    website: "",
    drive_folder_id: "",
    color: "#6366f1",
    active: "true",
  })

  const allErrors = Object.values(errors)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/customers")
  }

  return (
    <>
      <Head title="New Customer - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/customers">Customers</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New Customer</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/customers">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">New Customer</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <div className="rounded-xl border bg-card">
              <div className="p-6">
                {allErrors.length > 0 && (
                  <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                    <ul className="space-y-1 text-sm text-destructive">
                      {allErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2 space-y-2">
                      <Label htmlFor="name">Display Name</Label>
                      <Input id="name" value={data.name} onChange={(e) => setData("name", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="legal_name">Legal Name</Label>
                      <Input
                        id="legal_name"
                        value={data.legal_name}
                        onChange={(e) => setData("legal_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="alias">Alias</Label>
                      <Input id="alias" value={data.alias} onChange={(e) => setData("alias", e.target.value)} />
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Contact Name</Label>
                      <Input
                        id="contact_name"
                        value={data.contact_name}
                        onChange={(e) => setData("contact_name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Contact Email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={data.contact_email}
                        onChange={(e) => setData("contact_email", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Phone</Label>
                      <Input
                        id="contact_phone"
                        type="tel"
                        value={data.contact_phone}
                        onChange={(e) => setData("contact_phone", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={data.website}
                        onChange={(e) => setData("website", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="drive_folder_id">Drive folder ID</Label>
                      <Input
                        id="drive_folder_id"
                        value={data.drive_folder_id}
                        onChange={(e) => setData("drive_folder_id", e.target.value)}
                        placeholder="1AbC..."
                      />
                      <p className="text-muted-foreground text-xs">
                        Shared Drive folder for this customer. Invoices are filed under a per-year subfolder.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="tax_no">Tax Number</Label>
                      <Input id="tax_no" value={data.tax_no} onChange={(e) => setData("tax_no", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="active">Status</Label>
                      <Select value={data.active} onValueChange={(v) => setData("active", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Active</SelectItem>
                          <SelectItem value="false">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="color">Brand Color</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={data.color}
                        onChange={(e) => setData("color", e.target.value)}
                        className="h-10 w-14 rounded-md border cursor-pointer"
                      />
                      <Input
                        value={data.color}
                        onChange={(e) => setData("color", e.target.value)}
                        placeholder="#6366f1"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <textarea
                      id="address"
                      rows={3}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      value={data.address}
                      onChange={(e) => setData("address", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />
              <div className="flex justify-end gap-2 p-4">
                <Button variant="ghost" asChild>
                  <a href="/customers">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Creating..." : "Create Customer"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

CustomerNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
